//External Imports
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { styled } from "styled-components";
import RecipePageSkeleton from "../components/RecipePageSkeleton";
import { usePostHog } from "@posthog/react";
import {
  fetchFailure,
  fetchStart,
  fetchSuccess,
  resetCurrentVideo,
} from "../redux/videoSlice";
//Internal Imports
import axios from "axios";
import Comments from "../components/Comments";
import RecipeChannelBar from "../components/RecipeChannelBar";
import RecipeDetails from "../components/RecipeDetails";
import RecipeInfos from "../components/RecipeInfos";
import RecipeInstructions from "../components/RecipeInstructions";
import RecipeRecommendations from "../components/RecipeRecommendations";
import SocialShare from "../components/SocialShare";
const Container = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 24px 0px 24px;
  flex-grow: inherit;
  align-self: stretch;
  overflow: hidden;
`;
const Content = styled.div`
  overflow: auto;
  flex: 3;
  min-height: 0px;
  padding-bottom: 24px;
`;
const VideoWrapper = styled.div`
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  aspect-ratio: ${({ $ratio }) => $ratio || "16 / 9"};
`;

const StyledIframe = styled.iframe`
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
`;

const StyledVideo = styled.video`
  display: block;
  width: 100%;
  height: 100%;
`;

const Hr = styled.hr`
  margin: 15px 0px;
  border: 0.5px solid ${({ theme }) => theme.colorSurface};
`;

const Secondary = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  overflow: auto;
  width: 402px;
  //flex: 2;
  padding-bottom: 24px;
`;
const Recipe = () => {
  const currentVideo = useSelector((state) => state.video.currentVideo);
  const loading = useSelector((state) => state.video.loading);
  const posthog = usePostHog();
  const videoRef = useRef(null);
  /*const currentVideo = {
    resttime: 0,
    _id: "64e5c8af05dc90fa7426eb86",
    userId: "64e5c59805dc90fa7426eb61",
    title: "Cast Iron Pork Steak | Makin' It! Episode 5 | Brad Leone",
    desc: "This ain’t ya ma’s grocery store pork chops, ok gang?? I’m gonna teach you how and why to give a quality pork steak the same royal treatment you’d give a filet mignon. As our late great Jimi Hendrix once said, 'What you gonna do with that pork in your pan?' My two big pointers for cooking pork steak, or any meat products: 1. Don’t cheap out!! Spend some extra money and get some local and/or reputable pasture-raised meat, it makes all the difference (a few options below) 2. Pre-salt ya meat - let it hang out in the fridge overnight so it absorbs the sodium, it will give you a crisper sear",
    imgURL:
      "https://i.ytimg.com/vi/2YYZRqeUZtk/hqdefault.jpg?sqp=-oaymwEnCNACELwBSFryq4qpAxkIARUAAIhCGAHYAQHiAQoIGhACGAYgATgB&rs=AOn4CLDArrRZN6pz0sptmXrrdbXfyBpYag",
    videoURL: "https://www.youtube.com/embed/2YYZRqeUZtk",
    views: 0,
    tags: ["Cast Iron", "Steak", "Pork"],
    likes: ["64c94306fe3e7f0f55a70ca6"],
    dislikes: [],
    totaltime: 80,
    cooktime: 40,
    preptime: 40,
    cuisine: "Wild West",
    method: "Frying",
    origin: "New Jersey",
    calories: 1234,
    fat: 40,
    carbonhydrates: 14,
    proteins: 67,
    salt: 17,
    yielding: 3,
    difficulty: "1",
    createdAt: "2023-08-23T08:51:59.015Z",
    updatedAt: "2023-09-04T14:18:06.786Z",
    __v: 0,
  };*/
  const dispatch = useDispatch();
  const path = useLocation().pathname.split("/")[2];
  console.log("LOAD");
  const fetchVideo = useCallback(() => {
    console.log("fetching video");
    dispatch(fetchStart());
    axios
      .get(`${process.env.REACT_APP_RECIPE_API_BASE_URL}/videos/find/${path}`, {
        withCredentials: true,
      })
      .then((response) => {
        dispatch(fetchSuccess(response.data));
        posthog?.capture("recipe_viewed", {
          recipe_id: response.data._id,
          recipe_title: response.data.title,
          cuisine: response.data.cuisine,
          difficulty: response.data.difficulty,
        });
      })
      .catch((error) => {
        console.error("Error fetching video:", error);
        dispatch(fetchFailure());
        posthog?.captureException(error, { recipe_id: path });
      });
    return () => {
      dispatch(resetCurrentVideo());
    };
  }, [path, dispatch, posthog]);

  useEffect(() => {
    // This function runs when the component mounts
    fetchVideo();

    // This function runs when the component unmounts
    return () => {
      dispatch(resetCurrentVideo());
    };
  }, [dispatch, fetchVideo, path]);
  //SCHEMA A
  /*const recipeStructuredData = {
    "@context": "https://schema.org/",
    "@type": "Recipe",
    name: currentVideo?.title || "Loading...",
    image: [
      //Add more images for resolutions
      currentVideo?.imgURL || "Loading...",
    ],
    author: {
      "@type": "Person",
      name: `Chef ${channel.name}`,
    },
    datePublished: currentVideo?.createdAt || "Loading...",
    description: currentVideo?.desc || "Loading...",
    prepTime: `PT${currentVideo?.preptime || "Loading..."}M`,
    cookTime: `CT${currentVideo?.cooktime || "Loading..."}M`,
    totalTime: `TT${currentVideo?.totaltime || "Loading..."}M`,
    keywords: currentVideo?.tags || "Loading...",
    recipeYield: currentVideo?.yielding || "Loading...",
    cookingMethod: currentVideo?.method || "Loading...",
    recipeCategory: "Recipe Category", //Add to Recipe Mongoose Schema
    recipeCuisine: currentVideo?.cuisine || "Loading...",
    nutrition: {
      "@type": "NutritionInformation",
      calories: `${currentVideo?.calories || "Loading..."} calories`,
    },
    recipeIngredient: ingredients.map(
      (ingredient) =>
        `${ingredient.quantity} ${ingredient.unit} ${ingredient.title}`,
    ),
    recipeInstructions: steps.map((step) => ({
      "@type": "HowToStep",
      name: step.title,
      text: step.desc,
      image: step.img,
      timestamp: step.time,
    })),
    aggregateRating: {
      // Add Aggregated Ratings to Mongoose Schema
      "@type": "AggregateRating",
      ratingValue: "5",
      ratingCount: "18",
    },
    difficulty: currentVideo?.difficulty || "Loading...",
    video: {
      //Map instructions for Video Snippets tbd
      "@type": "VideoObject",
      name: currentVideo?.title || "Loading...",
      description: currentVideo?.desc || "Loading...",
      thumbnailUrl: [
        // Add Thumbnail Resolutions src-set
        currentVideo?.imgURL || "Loading...",
      ],
      contentUrl: currentVideo?.videoURL || "Loading...",
      //embedUrl: "https://www.example.com/videoplayer?video=123",
      uploadDate: currentVideo?.createdAt || "Loading...",
      //duration: "PT1M33S",
      interactionStatistic: {
        "@type": "InteractionCounter",
        interactionType: { "@type": "WatchAction" },
        userInteractionCount: views,
      },
      expires: "2030-02-05T08:00:00+08:00", //How to handle ?
    },
  };*/
  const isYouTube = useMemo(
    () => /youtube\.com|youtu\.be/.test(currentVideo?.videoURL || ""),
    [currentVideo?.videoURL],
  );

  const embedURL = useMemo(() => {
    if (!currentVideo?.videoURL) return "";
    if (isYouTube) {
      const sep = currentVideo.videoURL.includes("?") ? "&" : "?";
      return `${currentVideo.videoURL}${sep}enablejsapi=1`;
    }
    return currentVideo.videoURL;
  }, [currentVideo?.videoURL, isYouTube]);

  const seekVideo = useCallback(
    (seconds) => {
      if (isYouTube) {
        const iframe = document.getElementById("recipe-video");
        if (!iframe) return;
        iframe.contentWindow.postMessage(
          JSON.stringify({
            event: "command",
            func: "seekTo",
            args: [seconds, true],
          }),
          "https://www.youtube.com",
        );
      } else {
        if (videoRef.current) videoRef.current.currentTime = seconds;
      }
    },
    [isYouTube],
  );

  const pauseVideo = useCallback(() => {
    if (isYouTube) {
      const iframe = document.getElementById("recipe-video");
      if (!iframe) return;
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
        "https://www.youtube.com",
      );
    } else {
      videoRef.current?.pause();
    }
  }, [isYouTube]);

  if (loading || !currentVideo) {
    return <RecipePageSkeleton />;
  }

  return (
    <Container>
      <Helmet>
        <title>
          {currentVideo.title ? `${currentVideo.title} | CookCook` : "CookCook"}
        </title>
        <meta name="description" content={currentVideo.desc} />
        <meta
          name="keywords"
          content={
            Array.isArray(currentVideo.tags)
              ? currentVideo.tags.join(", ")
              : currentVideo.tags ?? ""
          }
        />
        <meta name="robots" content="" />
        <link rel="canonical" href="http://localhost:3000/recipe" />
      </Helmet>
      <Content>
        <VideoWrapper $ratio={currentVideo.aspectRatio}>
          {isYouTube ? (
            <StyledIframe
              id="recipe-video"
              src={embedURL}
              title="CookCook video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <StyledVideo
              ref={videoRef}
              src={currentVideo.videoURL}
              controls
            />
          )}
        </VideoWrapper>
        <RecipeInfos currentVideo={currentVideo} />
        <RecipeChannelBar currentVideo={currentVideo} />
        <RecipeDetails currentVideo={currentVideo} />
        <Hr />
        <SocialShare currentVideo={currentVideo} />
        <Hr />
        <Comments currentVideo={currentVideo} />
      </Content>
      <Secondary>
        <RecipeInstructions currentVideo={currentVideo} onSeek={seekVideo} onPause={pauseVideo} />
        <RecipeRecommendations />
      </Secondary>
    </Container>
  );
};
export default Recipe;
